import React from 'react';
import { Delete } from '@mui/icons-material';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import dayjs from 'dayjs';

import type { Notification } from './notificationFromSuprSend';

type NotificationAction = NonNullable<
  Notification['message']['actions']
>[number];

const CREATED_ON_CLASS = 'notification-card-created-on';
const DELETE_BUTTON_CLASS = 'notification-card-delete';

const CardRoot = styled(Stack, {
  shouldForwardProp: (prop) =>
    prop !== 'isUnread' && prop !== 'isDeleteAlwaysVisible'
})<{ isUnread: boolean; isDeleteAlwaysVisible: boolean }>(
  ({ isUnread, isDeleteAlwaysVisible }) => ({
    position: 'relative',
    gap: '4px',
    padding: '12px 16px 12px 24px',
    borderBottom: '1px solid var(--palette-semantic-divider-primary)',
    backgroundColor: isUnread
      ? 'var(--palette-semantic-background-unread)'
      : 'var(--palette-semantic-background-base)',
    cursor: 'default',
    ...(!isDeleteAlwaysVisible && {
      [`& .${DELETE_BUTTON_CLASS}`]: { opacity: 0 },
      [`&:hover .${DELETE_BUTTON_CLASS}, & .${DELETE_BUTTON_CLASS}:focus-visible`]:
        { opacity: 1 },
      [`&:hover .${CREATED_ON_CLASS}, &:has(.${DELETE_BUTTON_CLASS}:focus-visible) .${CREATED_ON_CLASS}`]:
        { opacity: 0 }
    })
  })
);

const TrailingSlot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isDeleteAlwaysVisible'
})<{ isDeleteAlwaysVisible: boolean }>(({ isDeleteAlwaysVisible }) => ({
  display: isDeleteAlwaysVisible ? 'flex' : 'grid',
  alignItems: 'center',
  justifyItems: 'end',
  gap: '8px',
  flexShrink: 0,
  minHeight: '20px',
  ...(!isDeleteAlwaysVisible && {
    '& > *': { gridArea: '1 / 1' }
  })
}));

const UnreadDot = styled(Box)({
  position: 'absolute',
  left: '10px',
  top: '20px',
  width: '4px',
  height: '4px',
  borderRadius: '50%',
  backgroundColor: 'var(--palette-semantic-background-primary)'
});

const DeleteButton = styled(IconButton)({
  width: '24px',
  height: '24px',
  padding: 0,
  backgroundColor: 'var(--palette-semantic-background-secondary)',
  '&:hover': {
    backgroundColor: 'var(--palette-semantic-background-secondary-hover)'
  }
});

interface NotificationCardProps {
  notification: Notification;
  isDeleteAlwaysVisible?: boolean;
  onArchive: () => void;
  onInteract: () => void;
  onNavigate?: (path: string) => void;
}

const NotificationCard: React.FC<NotificationCardProps> = ({
  notification,
  isDeleteAlwaysVisible = false,
  onArchive,
  onInteract,
  onNavigate
}) => {
  const isUnread = !notification.read_on;
  const actions = notification.message.actions ?? [];

  const handleArchive = (event: React.MouseEvent) => {
    event.stopPropagation();
    onArchive();
  };

  const handleActionClick = (
    event: React.MouseEvent,
    action: NotificationAction
  ) => {
    event.stopPropagation();
    onInteract();
    if (action.open_in_new_tab) {
      window.open(action.url, '_blank');
      return;
    }
    const target = new URL(action.url, window.location.href);
    if (onNavigate && target.origin === window.location.origin) {
      onNavigate(`${target.pathname}${target.search}${target.hash}`);
      return;
    }
    window.location.href = action.url;
  };

  return (
    <CardRoot
      isUnread={isUnread}
      isDeleteAlwaysVisible={isDeleteAlwaysVisible}
      onClick={onInteract}
      aria-label={isUnread ? 'Unread notification' : 'Notification'}
    >
      {isUnread && <UnreadDot />}
      <Stack direction="row" alignItems="flex-start" gap={1}>
        <Typography
          flex={1}
          minWidth={0}
          color="var(--palette-semantic-text-primary)"
          fontWeight={500}
          fontSize="14px"
          lineHeight="20px"
        >
          {notification.message.title}
        </Typography>
        <TrailingSlot isDeleteAlwaysVisible={isDeleteAlwaysVisible}>
          <Typography
            className={CREATED_ON_CLASS}
            color="var(--palette-semantic-text-caption)"
            fontSize="12px"
            lineHeight="16px"
            whiteSpace="nowrap"
          >
            {dayjs(notification.created_on).format('MMM D h:mm A')}
          </Typography>
          <DeleteButton
            className={DELETE_BUTTON_CLASS}
            aria-label="Delete notification"
            onClick={handleArchive}
          >
            <Delete
              htmlColor="var(--palette-semantic-icon-tertiary)"
              sx={{ width: 16, height: 16 }}
            />
          </DeleteButton>
        </TrailingSlot>
      </Stack>
      <Typography
        color="var(--palette-semantic-text-primary)"
        fontSize="14px"
        lineHeight="20px"
      >
        {notification.message.body}
      </Typography>
      {notification.message.subtext && (
        <Typography
          color="var(--palette-semantic-text-secondary)"
          fontSize="12px"
          mt={1}
        >
          {notification.message.subtext}
        </Typography>
      )}
      {actions.length > 0 && (
        <Stack direction="row" gap={2}>
          {actions.map((action, index) => (
            <Button
              key={action.label}
              size="medium"
              variant="contained"
              color={index === 0 ? 'primary' : 'secondary'}
              onClick={(event) => handleActionClick(event, action)}
            >
              {action.label}
            </Button>
          ))}
        </Stack>
      )}
    </CardRoot>
  );
};

export default NotificationCard;
